import { fetcher } from '@yanolja-org/core-ui';
import { NolBridge } from '@yanolja-org/nol-bridge';
import { getYaNative } from '@yanolja-org/ya-native';

import type { EndpointConfig } from '@yanolja-org/core-ui';
import type { YaNative } from '@yanolja-org/ya-native';
import type { GetNolAccessTokenParam } from '@yanolja-org/ya-native/interfaces';

interface HomeNavigationBarResponse {
  cart: { count: number };
}

const promisifyGetNolAccessToken = (yaNative: YaNative) => {
  return new Promise<GetNolAccessTokenParam | null>((resolve) => {
    yaNative.getNolAccessToken((success, result) => {
      if (success && result) resolve(result);
      else resolve(null);
    });
  });
};

/**
 * App 환경 (yaNative + NolBridge) 에서 인증/디바이스 헤더를 구성.
 * 둘 중 하나라도 enabled 가 아니면 *web 환경* 으로 간주 — null 반환.
 * tokenInfo / appInfo 획득 실패는 throw 하지 않고 해당 헤더만 생략한다.
 */
const buildAppAuthHeaders = async (): Promise<Record<string, string> | null> => {
  const yaNative = getYaNative();

  if (!yaNative.isEnabled || !NolBridge.isEnabled) return null;

  const [tokenResult, appInfoResult] = await Promise.allSettled([
    promisifyGetNolAccessToken(yaNative),
    NolBridge.nolAppInfo(),
  ]);

  const tokenInfo = tokenResult.status === 'fulfilled' ? tokenResult.value : null;
  const appInfo = appInfoResult.status === 'fulfilled' ? appInfoResult.value : null;

  const headers: Record<string, string> = {};
  if (tokenInfo?.accessToken) headers['x-session-token'] = tokenInfo.accessToken;
  if (appInfo?.value['Nol-Platform']) headers['nol-platform'] = appInfo.value['Nol-Platform'];
  if (appInfo?.value.cgntId) headers.cgntId = appInfo.value.cgntId;

  return Object.keys(headers).length > 0 ? headers : null;
};

/**
 * NOL App Gateway 의 navigation-bar endpoint 에서 cart count 조회 → `{ hasItem }` 형태로 정규화.
 *
 * 환경 분기:
 * - **App** (`yaNative.isEnabled && NolBridge.isEnabled`): bridge / native 에서 token / platform / cgntId
 *   를 받아 헤더로 운반.
 * - **Web**: 위 헤더 없이 fetch — `prefix` 가 상대경로 (`/nol-api`) 라 same-origin → default credentials
 *   (`same-origin`) 로 cookie 가 자동 전송 (cookie 기반 인증).
 *
 * 앱 인증정보 획득 실패(token / appInfo)는 요청 자체를 막지 않고, 획득된 헤더만 포함해 진행.
 * 그 외 실패 케이스 (네트워크 / http / parse / abort 등) 는 `{ hasItem: false }` 로 fallback.
 * host (CoreUIDataProvider 의 fetchers.cart) 의 `signal` 을 통과 → upstream abort propagation.
 */
export const fetchCartStatusFromGateway = async ({
  prefix = '/nol-api',
  signal,
}: { prefix?: string; signal?: AbortSignal } = {}): Promise<{ hasItem: boolean }> => {
  try {
    const appHeaders = await buildAppAuthHeaders();

    const config: EndpointConfig = {
      url: `${prefix}/nol-display/navigation-bar`,
      headers: appHeaders ?? undefined,
    };

    const result = await fetcher<HomeNavigationBarResponse>(config, signal ?? new AbortController().signal);

    if (result.kind === 'success') return { hasItem: result.data.cart.count > 0 };
    return { hasItem: false };
  } catch (error) {
    return { hasItem: false };
  }
};
